import {
  Award,
  Ban,
  CalendarCheck2,
  CalendarDays,
  CalendarPlus,
  ClipboardList,
  Clock,
  Eye,
  Flag,
  Grid3x3,
  Medal,
  Network,
  Pencil,
  Play,
  Shuffle,
  Trophy,
  Users,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelectedClub } from '@/features/club';
import {
  BEST_OF_LABELS,
  COMPETITION_CATEGORY_LABELS,
  COMPETITION_SYSTEM_LABELS,
  countPlayingSlots,
  DISCIPLINE_CODE_LABELS,
  drawKindOf,
  formatDateRange,
  MATCH_TYPE_LABELS,
  SECOND_PHASE_LABELS,
  useCompetitionActionMutation,
  useCompetitionQuery,
  useParticipantsQuery,
  VISIBILITY_LABELS,
  type CompetitionActionVariables,
} from '@/features/competition';
import { ApiError } from '@/shared/api/ApiError';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Modal,
  Skeleton,
  useToast,
} from '@/shared/ui';

/** Pojedynczy wiersz podsumowania: ikona, etykieta, wartość. */
function InfoRow({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-fg-subtle mt-0.5 shrink-0 [&_svg]:size-4">{icon}</span>
      <div className="min-w-0">
        <dt className="text-fg-muted text-xs">{label}</dt>
        <dd className="text-fg-strong text-sm font-semibold">{value}</dd>
      </div>
    </div>
  );
}

/**
 * Zakładka „Informacje" centrum turnieju: podsumowanie ustawień, edycja
 * (do losowania) i akcje cyklu życia — otwarcie/zamknięcie zapisów,
 * rozpoczęcie (Challenge) i zakończenie turnieju, anulowanie.
 */
export function TournamentInfoPage() {
  const { t } = useTranslation('tournament');
  const { competitionId = '' } = useParams<{ competitionId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { clubId } = useSelectedClub();

  const competitionQuery = useCompetitionQuery(competitionId);
  const participantsQuery = useParticipantsQuery(competitionId);
  const actionMutation = useCompetitionActionMutation(clubId);

  const [confirmCancel, setConfirmCancel] = useState(false);

  const competition = competitionQuery.data;
  const participants = useMemo(() => participantsQuery.data ?? [], [participantsQuery.data]);

  if (!competition) {
    return <Skeleton className="h-96 w-full" />;
  }

  // Licznik pokazuje obsadę turnieju, czyli zgłoszenia mieszczące się w limicie.
  // Rezerwa należy do ekranu dodawania zawodników — tam się nią zarządza — więc
  // tutaj jej nie ma; inaczej ta sama rozgrywka miałaby dwie różne „liczby graczy”.
  const playing = countPlayingSlots(participants, competition.maxPlayers);
  const drawKind = drawKindOf(competition.system);
  const editable =
    competition.status === 'DRAFT' ||
    competition.status === 'AWAITING_PAYMENT' ||
    competition.status === 'REGISTRATION_OPEN' ||
    competition.status === 'REGISTRATION_CLOSED';

  /** Komunikat błędu z API albo tekst zastępczy. */
  function describe(error: unknown, fallback: string): string {
    return error instanceof ApiError ? error.message : fallback;
  }

  function runAction(action: CompetitionActionVariables['action'], successMessage: string) {
    actionMutation.mutate(
      { competitionId, action },
      {
        onSuccess: () => {
          setConfirmCancel(false);
          showToast({ tone: 'success', description: successMessage });
        },
        onError: (error) => {
          setConfirmCancel(false);
          showToast({ tone: 'danger', description: describe(error, t('list.toast.error')) });
        },
      },
    );
  }

  return (
    <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_19rem]">
      {/* Ustawienia rozgrywki */}
      <Card padding="lg">
        <CardHeader
          title={t('infoTab.settings')}
          className="mb-5"
          {...(editable
            ? {
                action: (
                  <Button
                    size="sm"
                    variant="secondary"
                    leadingIcon={<Pencil className="size-4" />}
                    onClick={() => void navigate(`/turnieje/${competitionId}/edycja`)}
                  >
                    {t('infoTab.editButton')}
                  </Button>
                ),
              }
            : {})}
        />

        {!editable && competition.status !== 'CANCELLED' && (
          <p className="text-fg-subtle -mt-3 mb-4 text-xs">{t('infoTab.editLockedHint')}</p>
        )}

        <dl className="grid gap-5 sm:grid-cols-2">
          <InfoRow icon={<ClipboardList />} label={t('infoTab.name')} value={competition.name} />
          <InfoRow
            icon={<Trophy />}
            label={t('infoTab.discipline')}
            value={DISCIPLINE_CODE_LABELS[competition.discipline] ?? competition.discipline}
          />
          <InfoRow
            icon={<Network />}
            label={t('infoTab.system')}
            value={COMPETITION_SYSTEM_LABELS[competition.system] ?? competition.system}
          />
          <InfoRow
            icon={<Users />}
            label={t('infoTab.matchType')}
            value={MATCH_TYPE_LABELS[competition.matchType] ?? competition.matchType}
          />
          {competition.system === 'GROUP_PLAYOFF' && (
            <InfoRow
              icon={<Grid3x3 />}
              label={t('infoTab.secondPhase')}
              value={
                competition.secondPhaseType
                  ? SECOND_PHASE_LABELS[competition.secondPhaseType]
                  : t('infoTab.empty')
              }
            />
          )}
          <InfoRow
            icon={<Award />}
            label={t('infoTab.category')}
            value={
              competition.category
                ? (COMPETITION_CATEGORY_LABELS[competition.category] ?? competition.category)
                : t('infoTab.empty')
            }
          />
          <InfoRow
            icon={<Eye />}
            label={t('infoTab.visibility')}
            value={VISIBILITY_LABELS[competition.visibility] ?? competition.visibility}
          />
          <InfoRow
            icon={<CalendarDays />}
            label={t('infoTab.term')}
            value={formatDateRange(competition.startDate, competition.endDate)}
          />
          <InfoRow
            icon={<Users />}
            label={t('infoTab.players')}
            value={
              <>
                {t('infoTab.playersValue', {
                  count: playing,
                  max: competition.maxPlayers,
                })}
              </>
            }
          />
          {competition.scoringRules && (
            <InfoRow
              icon={<Clock />}
              label={t('infoTab.matchFormat')}
              value={
                BEST_OF_LABELS[String(competition.scoringRules.bestOf)] ??
                `Best of ${competition.scoringRules.bestOf}`
              }
            />
          )}
          {(drawKind === 'bracket' ||
            (drawKind === 'groups' && competition.secondPhaseType !== 'FINAL_GROUP')) && (
            <InfoRow
              icon={<Medal />}
              label={t('infoTab.thirdPlace')}
              value={competition.thirdPlaceMatch ? t('infoTab.yes') : t('infoTab.no')}
            />
          )}
          {competition.description && (
            <div className="sm:col-span-2">
              <InfoRow
                icon={<ClipboardList />}
                label={t('infoTab.description')}
                value={<span className="font-normal">{competition.description}</span>}
              />
            </div>
          )}
        </dl>
      </Card>

      {/* Zarządzanie cyklem życia */}
      <div className="flex flex-col gap-4 xl:sticky xl:top-6">
        <Card padding="lg" className="flex flex-col gap-3">
          <CardHeader title={t('infoTab.actions')} className="mb-0" />
          <p className="text-fg-muted text-sm">
            {t(`infoTab.lifecycleHint.${competition.status === 'AWAITING_PAYMENT' ? 'DRAFT' : competition.status}`)}
          </p>

          {competition.status === 'DRAFT' && (
            <Button
              leadingIcon={<CalendarPlus className="size-4" />}
              loading={actionMutation.isPending}
              onClick={() => runAction('open-registration', t('list.toast.openRegistration', { name: competition.name }))}
            >
              {t('list.actions.openRegistration')}
            </Button>
          )}

          {competition.status === 'REGISTRATION_OPEN' && (
            <Button
              leadingIcon={<CalendarCheck2 className="size-4" />}
              loading={actionMutation.isPending}
              onClick={() => runAction('close-registration', t('list.toast.closeRegistration', { name: competition.name }))}
            >
              {t('list.actions.closeRegistration')}
            </Button>
          )}

          {competition.status === 'REGISTRATION_CLOSED' &&
            (drawKind !== 'none' ? (
              <Button
                leadingIcon={<Shuffle className="size-4" />}
                onClick={() => void navigate(`/turnieje/${competitionId}/losowanie`)}
              >
                {t('infoTab.goToDraw')}
              </Button>
            ) : (
              <Button
                leadingIcon={<Play className="size-4" />}
                loading={actionMutation.isPending}
                onClick={() => runAction('start', t('list.toast.start', { name: competition.name }))}
              >
                {t('list.actions.start')}
              </Button>
            ))}

          {competition.status === 'IN_PROGRESS' && (
            <Button
              leadingIcon={<Flag className="size-4" />}
              loading={actionMutation.isPending}
              onClick={() => runAction('complete', t('list.toast.complete', { name: competition.name }))}
            >
              {t('list.actions.complete')}
            </Button>
          )}

          {competition.status !== 'COMPLETED' && competition.status !== 'CANCELLED' && (
            <Button
              variant="danger"
              leadingIcon={<Ban className="size-4" />}
              onClick={() => setConfirmCancel(true)}
            >
              {t('list.actions.cancel')}
            </Button>
          )}
        </Card>
      </div>

      <Modal
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        size="sm"
        title={t('list.confirmCancel.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmCancel(false)}>
              {t('list.confirmCancel.keep')}
            </Button>
            <Button
              variant="danger"
              loading={actionMutation.isPending}
              onClick={() => runAction('cancel', t('list.toast.cancel', { name: competition.name }))}
            >
              {t('list.confirmCancel.confirm')}
            </Button>
          </>
        }
      >
        {t('list.confirmCancel.body', { name: competition.name })}
      </Modal>

      {competition.status === 'CANCELLED' && (
        <Alert tone="warning" className="xl:col-span-2">
          {t('infoTab.lifecycleHint.CANCELLED')}
        </Alert>
      )}
    </div>
  );
}
